/**
 * Carbon Theme Provider for Sigma08 Educational Platform
 * Provides IBM Carbon Design System theming throughout the application
 */

import React from 'react';
import { Theme } from '@carbon/react';
import { sigma08CarbonTheme, carbonCSSVariables } from '../../theme/carbonTheme';
import { useTheme } from '../../contexts/ThemeContext';

interface CarbonThemeProviderProps {
  children: React.ReactNode;
}

/**
 * Carbon Theme Provider Component
 * Wraps the application with Carbon Design System theme configuration
 */
export const CarbonThemeProvider: React.FC<CarbonThemeProviderProps> = ({ children }) => {
  const { theme } = useTheme();

  // Apply CSS custom properties for Carbon theme
  React.useEffect(() => {
    const root = document.documentElement;

    // Apply Carbon CSS variables to document root
    Object.entries(carbonCSSVariables).forEach(([property, value]) => {
      root.style.setProperty(property, value as string);
    });

    // Minimal focus enhancements that complement Carbon's default focus management
    const focusStyles = `
      /* Skip focus outline on mouse interactions to complement Carbon defaults */
      .mouse-user *:focus {
        outline: none;
      }

      /* Ensure accessibility compliance for custom components that don't use Carbon defaults */
      .sigma08-custom-focus:focus {
        outline: 2px solid var(--cds-focus, #0043ce);
        outline-offset: 2px;
      }

      /* Ensure proper spacing using Carbon tokens for any custom forms */
      .sigma08-form-spacing .cds--form-item {
        margin-bottom: var(--cds-spacing-06, 1.5rem);
      }

      .sigma08-form-spacing .cds--fieldset {
        margin-bottom: var(--cds-spacing-07, 2rem);
      }
    `;

    // Inject focus styles
    let focusStyleSheet = document.getElementById('carbon-focus-styles');
    if (!focusStyleSheet) {
      focusStyleSheet = document.createElement('style');
      focusStyleSheet.id = 'carbon-focus-styles';
      document.head.appendChild(focusStyleSheet);
    }
    focusStyleSheet.textContent = focusStyles;

    // Add keyboard navigation detection
    const addKeyboardClass = () => document.body.classList.add('keyboard-user');
    const addMouseClass = () => document.body.classList.add('mouse-user');

    document.addEventListener('keydown', addKeyboardClass);
    document.addEventListener('mousedown', addMouseClass);

    // Remove all theme classes first
    document.body.classList.remove('cds--theme--white', 'cds--theme--g10', 'cds--theme--g90', 'cds--theme--g100');

    // Add current Carbon theme class to body
    document.body.classList.add(`cds--theme--${theme}`);

    // Cleanup function
    return () => {
      // Remove Carbon CSS variables
      Object.keys(carbonCSSVariables).forEach(property => {
        root.style.removeProperty(property);
      });

      // Remove focus styles
      const styleSheet = document.getElementById('carbon-focus-styles');
      if (styleSheet) {
        styleSheet.remove();
      }

      // Remove event listeners
      document.removeEventListener('keydown', addKeyboardClass);
      document.removeEventListener('mousedown', addMouseClass);

      // Remove all Carbon theme classes
      document.body.classList.remove('cds--theme--white', 'cds--theme--g10', 'cds--theme--g90', 'cds--theme--g100');
      document.body.classList.remove('keyboard-user', 'mouse-user');
    };
  }, [theme]);

  return (
    <Theme theme={theme}>
      <div className="sigma08-theme-container" data-carbon-theme={theme} style={{ minHeight: '100vh' }}>
        {children}
      </div>
    </Theme>
  );
};

export default CarbonThemeProvider;
